<script setup lang="ts">
import { useHead } from '@unhead/vue';
import { Header, Footer, UiFullLoader } from '@/components/ui';
import { InfoModel } from '@/graphql';
import { convertUrl } from '@/utils/convertUrl';

import { useGetInfoList } from './composables';

import { ref, watch, useRoute, showError } from '#imports';

const route = useRoute();
const { data, isLoading } = useGetInfoList();

const infoRef = ref<InfoModel>();
const titleRef = ref<string>();
const seoTitleRef = ref<string>();
const seoDescriptionRef = ref<string>();

watch(
  data,
  () => {
    if (data.value) {
      infoRef.value = data.value?.find(({ id }) => {
        return id === route.params.id;
      });
      if (!infoRef.value) {
        showError({
          statusCode: 404,
          statusMessage: 'Page Not Found',
        });
      }
    }
  },
  { immediate: true }
);

watch(infoRef, () => {
  if (infoRef.value) {
    titleRef.value = infoRef.value.title as string;
    seoTitleRef.value = infoRef.value.seo_title?.plaintext as string;
    seoDescriptionRef.value = infoRef.value.seo_description?.plaintext as string;

    useHead({
      title: seoTitleRef.value ? seoTitleRef.value : '',
      meta: [
        { name: 'description', content: seoDescriptionRef.value ? seoDescriptionRef.value : '' },
        { name: 'keywords', content: 'dスマホローン,ドコモ' },
        { property: 'og:url', content: 'https://loan.docomo.ne.jp/info/' + route.params.id + '/' },
        { property: 'og:type', content: 'article' },
        { property: 'og:title', content: seoTitleRef.value ? seoTitleRef.value : '' },
        { property: 'og:description', content: seoDescriptionRef.value ? seoDescriptionRef.value : '' },
        { property: 'og:site_name', content: 'dスマホローン | ドコモ' },
        { property: 'og:image', content: 'https://loan.docomo.ne.jp/img/ogp.png' },
      ],
      link: [
        {
          rel: 'canonical',
          href: 'https://loan.docomo.ne.jp/' + route.params.id,
        },
        {
          rel: 'icon',
          href: '/img/favicon.ico',
        },
        {
          rel: 'icon',
          type: 'image/png',
          href: '/img/android-chrome-192x192.png',
        },
        {
          rel: 'apple-touch-icon',
          href: '/img/apple-touch-icon.png',
        },
      ],
    });
  }
});

const D_CARD_CASHING =
  'ｄカードのキャッシングをご利用中のお客さまへの注意事項は<a class="arrow_icon arrow_icon02" href="/apply/#anc_02">こちら</a>';
</script>

<template>
  <Header />
  <UiFullLoader v-if="isLoading" />
  <template v-else>
    <div class="isp hbg_bg"></div>
    <nav class="breadcrumb">
      <div class="breadcrumb_inr">
        <ol class="breadcrumb_list" itemscope itemtype="https://schema.org/BreadcrumbList">
          <li class="breadcrumb_item" itemprop="itemListElement" itemscope itemtype="https://schema.org/ListItem">
            <a itemprop="item" href="/"><span itemprop="name">トップ</span></a
            ><meta itemprop="position" content="1" />
          </li>
          <span>&nbsp;</span>
          <li class="breadcrumb_item" itemprop="itemListElement" itemscope itemtype="https://schema.org/ListItem">
            <a itemprop="item" href="/info/"><span itemprop="name" data-testid="infoListUrl">お知らせ一覧</span></a
            ><meta itemprop="position" content="2" />
          </li>
          <span>&nbsp;</span>
          <li class="breadcrumb_item" itemprop="itemListElement" itemscope itemtype="https://schema.org/ListItem">
            <span itemprop="name">{{ titleRef }}</span
            ><meta itemprop="position" content="4" />
          </li>
        </ol>
      </div>
    </nav>

    <!-- eslint-disable-next-line vue/no-v-html -->
    <main class="main" v-html="infoRef?.body?.plaintext"></main>

    <section class="section02">
      <!--#include virtual="/focus/ssi/cv_area.html" -->
      <ul class="cv_card cv_btnArea">
        <li class="cv_card_item">
          <h2 class="title_lv2">新規お申込み</h2>
          <div class="cv_card_text_area">
            <p class="cv_card_text pctAl sptAc">
              <span class="ask"><span class="emphasis">お申込み</span>から<span class="emphasis">ご契約</span>まで</span
              ><span class="ask"><span class="emphasis">Web<span>で</span>完結！</span></span>
            </p>
          </div>
          <div class="cv_card_btn01">
            <p class="top_btnItem btnType02 btn_icon btn_icon01 cv_card_btn fwb">
              <a
                :href="`${convertUrl()}common/login/cushionWithUniversalLinkActivation?prompt=relogin&transitionSourceId=basic`"
                >今すぐお申込み<br /><span>（dアカウントでログイン）</span></a
              >
            </p>
            <ul class="listType01 tAl mt10">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <li v-html="D_CARD_CASHING"></li>
            </ul>
          </div>
        </li>
        <li class="cv_card_item">
          <h2 class="title_lv2">簡易シミュレーション</h2>
          <div class="cv_card_btn02">
            <p class="top_btnItem btnType03 btn_icon btn_icon04 cv_card_btn fwb">
              <a href="/apply/1second/"><span>お借入可能？優遇金利は何％？</span>簡単1秒診断</a>
            </p>
          </div>
          <div class="cv_card_btn02 mt10">
            <p class="top_btnItem btnType03 btn_icon btn_icon02 cv_card_btn fwb">
              <a href="/borrow/simulation/"
                ><span>毎月のご返済額はいくらぐらい？</span
                ><span class="fs18"><span class="ask">ご返済</span><span class="ask">シミュレーション</span></span></a
              >
            </p>
          </div>
        </li>
      </ul>
    </section>
    <!-- /.section02 -->
    <Footer />
  </template>
</template>
